import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserPublic } from '@/types';

interface AuthState {
  user: UserPublic | null;
  token: string | null;
  isAuthenticated: boolean;
  // Hydration state - tracks if Zustand has finished loading from localStorage
  _hasHydrated: boolean;
  // TOTP pending state
  pendingTotpToken: string | null;
  pendingTotpEmail: string | null;
  // Actions
  login: (user: UserPublic, token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<UserPublic>) => void;
  // TOTP actions
  setTotpPending: (tempToken: string, email: string) => void;
  clearTotpPending: () => void;
  // Hydration action
  setHasHydrated: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      _hasHydrated: false,
      pendingTotpToken: null,
      pendingTotpEmail: null,

      login: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          pendingTotpToken: null,
          pendingTotpEmail: null,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          pendingTotpToken: null,
          pendingTotpEmail: null,
        }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      setTotpPending: (tempToken, email) =>
        set({
          pendingTotpToken: tempToken,
          pendingTotpEmail: email,
        }),

      clearTotpPending: () =>
        set({
          pendingTotpToken: null,
          pendingTotpEmail: null,
        }),

      setHasHydrated: (value) =>
        set({ _hasHydrated: value }),
    }),
    {
      name: 'nspm-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        // Don't persist _hasHydrated or TOTP pending state
      }),
      onRehydrateStorage: () => (state) => {
        // Called when hydration from localStorage is complete
        state?.setHasHydrated(true);
      },
    }
  )
);
